import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { useUiStore } from '../../stores/uiStore.js';
import { useMakeMap } from '../../hooks/useMakeMap.js';
import { Badge } from '../ui/index.js';
import { DaySceneList } from './DaySceneList.js';
import type { Day } from '../../types/index.js';

interface DayCardProps {
  day: Day;
}

export function DayCard({ day }: DayCardProps) {
  const [expanded, setExpanded] = useState(false);
  const qc = useQueryClient();
  const { openModal } = useUiStore();
  const { setActiveDay, activeDay } = useMapStore();
  const makeMap = useMakeMap();

  const isActive = activeDay?.id === day.id;

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/days/${day.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['days', day.projectId] });
      if (isActive) setActiveDay(null);
      toast.success('Day deleted');
    },
  });

  const formatDate = (d?: string) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : null;

  return (
    <div className={`rounded-xl border transition-all ${isActive ? 'border-brand-400' : 'border-gray-200'}`}>
      <div
        className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50 rounded-xl"
        onClick={() => { setExpanded(!expanded); setActiveDay(isActive ? null : day); }}
      >
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">{day.name}</span>
            {day.shootDate && <Badge color="blue">{formatDate(day.shootDate)}</Badge>}
          </div>
          {day.notes && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{day.notes}</p>}
        </div>
        <div className="flex gap-1 items-center" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => makeMap(day)}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-600 text-xs"
            title="Make Map — fit view to this day's locations"
          >🗺️</button>
          <button onClick={() => openModal('dayForm', day)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 text-xs">✎</button>
          <button onClick={() => { if (confirm('Delete this day?')) deleteMutation.mutate(); }} className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 text-xs">✕</button>
          <span className="text-gray-400 text-xs ml-1">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 px-3 pb-3">
          <DaySceneList dayId={day.id} />
        </div>
      )}
    </div>
  );
}
